import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

const RESOLUTION_COLORS: Record<string, string> = {
  use_local: '#3b82f6',
  use_external: '#8b5cf6',
  use_merged: '#10b981',
};

const STRATEGY_COLORS: Record<string, string> = {
  use_local: '#3b82f6',
  use_external: '#8b5cf6',
  newest_wins: '#f59e0b',
  manual: '#6b7280',
};

interface ConflictRecord {
  id: string;
  org_id: string;
  connection_id: string;
  entity_type: string;
  entity_id: string;
  external_id: string;
  local_version: Record<string, unknown>;
  external_version: Record<string, unknown>;
  resolution: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

interface ConflictRule {
  id: string;
  org_id: string;
  connection_id: string;
  entity_type: string;
  field_pattern: string;
  resolution_strategy: string;
  created_at: string;
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: `${color}18`,
      color,
      textTransform: 'capitalize',
    }}>
      {label.replace(/_/g, ' ')}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: 8,
      padding: '1rem 1.25rem',
      border: '1px solid #e5e7eb',
      minWidth: 140,
    }}>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{value}</div>
    </div>
  );
}

export default function ConflictsPage() {
  const [conflicts, setConflicts] = useState<ConflictRecord[]>([]);
  const [rules, setRules] = useState<ConflictRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [resolveModal, setResolveModal] = useState<ConflictRecord | null>(null);
  const [resolution, setResolution] = useState('use_local');
  const [mergedJson, setMergedJson] = useState('');
  const [resolveError, setResolveError] = useState('');

  // Rule creation
  const [newRuleConnId, setNewRuleConnId] = useState('*');
  const [newRuleEntityType, setNewRuleEntityType] = useState('*');
  const [newRulePattern, setNewRulePattern] = useState('*');
  const [newRuleStrategy, setNewRuleStrategy] = useState('newest_wins');

  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState('');

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const resolved = showResolved ? 'all' : 'false';
      const res = await api.get<{ data: ConflictRecord[] }>(`/conflicts?resolved=${resolved}`);
      setConflicts(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [showResolved]);

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get<{ data: ConflictRule[] }>('/conflicts/rules');
      setRules(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchConflicts(); }, [fetchConflicts]);
  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleResolve = async () => {
    if (!resolveModal) return;
    let body: Record<string, unknown> = { resolution };
    if (resolution === 'use_merged') {
      try {
        body.merged_data = JSON.parse(mergedJson);
      } catch {
        setResolveError('Invalid JSON for merged data');
        return;
      }
    }
    setActionLoading('resolve');
    try {
      await api.post(`/conflicts/${resolveModal.id}/resolve`, body);
      setResolveModal(null);
      await fetchConflicts();
    } catch (e: any) {
      setResolveError(e.message || 'Resolve failed');
    }
    setActionLoading(null);
  };

  const handleAutoResolve = async () => {
    setActionLoading('auto-resolve');
    try {
      const res = await api.post<{ data: { resolved: number; skipped: number; total: number } }>('/conflicts/auto-resolve', {});
      alert(`Auto-resolved ${res.data.resolved} of ${res.data.total} conflicts (${res.data.skipped} skipped)`);
      await fetchConflicts();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleCreateRule = async () => {
    setActionLoading('create-rule');
    try {
      await api.post('/conflicts/rules', {
        connection_id: newRuleConnId,
        entity_type: newRuleEntityType,
        field_pattern: newRulePattern,
        resolution_strategy: newRuleStrategy,
      });
      await fetchRules();
      setNewRuleConnId('*');
      setNewRuleEntityType('*');
      setNewRulePattern('*');
      setNewRuleStrategy('newest_wins');
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleDeleteRule = async (ruleId: string) => {
    setActionLoading(ruleId);
    try {
      await api.delete(`/conflicts/rules/${ruleId}`);
      await fetchRules();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const openResolveModal = (conflict: ConflictRecord) => {
    setResolveModal(conflict);
    setResolution('use_local');
    setMergedJson(JSON.stringify({ ...conflict.local_version, ...conflict.external_version }, null, 2));
    setResolveError('');
  };

  const filtered = entityTypeFilter
    ? conflicts.filter(c => c.entity_type === entityTypeFilter)
    : conflicts;

  const unresolved = conflicts.filter(c => c.resolution === null).length;
  const entityTypes = [...new Set(conflicts.map(c => c.entity_type))];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Sync Conflicts</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Review and resolve data conflicts from sync operations
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleAutoResolve}
            disabled={actionLoading === 'auto-resolve' || unresolved === 0}
            style={{ ...btnStyle, backgroundColor: ACCENT, color: '#fff' }}
          >
            {actionLoading === 'auto-resolve' ? 'Resolving...' : `Auto-Resolve (${unresolved})`}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <StatCard label="Total Conflicts" value={conflicts.length} />
        <StatCard label="Unresolved" value={unresolved} />
        <StatCard label="Entity Types" value={entityTypes.length} />
        <StatCard label="Auto-Resolve Rules" value={rules.length} />
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={entityTypeFilter}
          onChange={e => setEntityTypeFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">All Entity Types</option>
          {entityTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showResolved}
            onChange={e => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>
      </div>

      {/* Conflicts table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto', marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={thStyle}>Entity Type</th>
              <th style={thStyle}>External ID</th>
              <th style={thStyle}>Differing Fields</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No conflicts found</td></tr>
            ) : filtered.map(c => {
              const diffFields = getDifferingFields(c.local_version, c.external_version);
              return (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{c.entity_type}</td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.external_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      {diffFields.slice(0, 3).join(', ')}
                      {diffFields.length > 3 ? ` +${diffFields.length - 3} more` : ''}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {c.resolution ? (
                      <Badge label={c.resolution} color={RESOLUTION_COLORS[c.resolution] || '#10b981'} />
                    ) : (
                      <Badge label="unresolved" color="#ef4444" />
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {!c.resolution && (
                      <button
                        onClick={() => openResolveModal(c)}
                        style={{ ...smallBtnStyle, color: ACCENT, borderColor: ACCENT }}
                      >
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rules panel */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setShowRules(!showRules)}
          style={{
            width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none',
            cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '0.875rem', fontWeight: 600, color: '#111827',
          }}
        >
          <span>Auto-Resolution Rules ({rules.length})</span>
          <span>{showRules ? '\u25B2' : '\u25BC'}</span>
        </button>
        {showRules && (
          <div style={{ padding: '0 1rem 1rem' }}>
            {/* Create rule form */}
            <div style={{
              display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end',
            }}>
              <div>
                <label style={labelStyle}>Connection ID</label>
                <input
                  type="text"
                  value={newRuleConnId}
                  onChange={e => setNewRuleConnId(e.target.value)}
                  placeholder="* for all"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Entity Type</label>
                <input
                  type="text"
                  value={newRuleEntityType}
                  onChange={e => setNewRuleEntityType(e.target.value)}
                  placeholder="* for all"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Field Pattern</label>
                <input
                  type="text"
                  value={newRulePattern}
                  onChange={e => setNewRulePattern(e.target.value)}
                  placeholder="* for all"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Strategy</label>
                <select
                  value={newRuleStrategy}
                  onChange={e => setNewRuleStrategy(e.target.value)}
                  style={selectStyle}
                >
                  <option value="newest_wins">Newest Wins</option>
                  <option value="use_local">Use Local</option>
                  <option value="use_external">Use External</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <button
                onClick={handleCreateRule}
                disabled={actionLoading === 'create-rule'}
                style={{ ...btnStyle, backgroundColor: ACCENT, color: '#fff' }}
              >
                {actionLoading === 'create-rule' ? 'Creating...' : 'Add Rule'}
              </button>
            </div>

            {/* Existing rules */}
            {rules.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No rules configured yet.</p>
            ) : rules.map(r => (
              <div key={r.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.625rem 0.75rem', borderRadius: 6, border: '1px solid #f3f4f6',
                marginBottom: '0.5rem', backgroundColor: '#fafafa',
              }}>
                <div style={{ fontSize: '0.875rem' }}>
                  <span style={{ fontWeight: 600 }}>{r.entity_type}</span>
                  <span style={{ color: '#6b7280' }}> on </span>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.connection_id}</span>
                  <span style={{ color: '#6b7280' }}> (pattern: {r.field_pattern}) </span>
                  <Badge label={r.resolution_strategy} color={STRATEGY_COLORS[r.resolution_strategy] || '#6b7280'} />
                </div>
                <button
                  onClick={() => handleDeleteRule(r.id)}
                  disabled={actionLoading === r.id}
                  style={{ ...smallBtnStyle, color: '#ef4444', borderColor: '#ef4444' }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resolve modal */}
      {resolveModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: '1.5rem',
            width: '90%', maxWidth: 700, maxHeight: '85vh', overflow: 'auto',
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
              Resolve Conflict: {resolveModal.entity_type}
            </h2>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
              External ID: {resolveModal.external_id}
            </p>

            {/* Side-by-side comparison */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ ...labelStyle, color: '#3b82f6' }}>Local Version</label>
                <pre style={preStyle}>{JSON.stringify(resolveModal.local_version, null, 2)}</pre>
              </div>
              <div>
                <label style={{ ...labelStyle, color: '#8b5cf6' }}>External Version</label>
                <pre style={preStyle}>{JSON.stringify(resolveModal.external_version, null, 2)}</pre>
              </div>
            </div>

            {/* Resolution choice */}
            <label style={labelStyle}>Resolution</label>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              {(['use_local', 'use_external', 'use_merged'] as const).map(opt => (
                <label key={opt} style={{
                  display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem',
                  cursor: 'pointer', padding: '6px 12px', borderRadius: 6,
                  border: resolution === opt ? `2px solid ${ACCENT}` : '1px solid #d1d5db',
                  backgroundColor: resolution === opt ? `${ACCENT}08` : '#fff',
                }}>
                  <input
                    type="radio"
                    name="resolution"
                    value={opt}
                    checked={resolution === opt}
                    onChange={() => setResolution(opt)}
                    style={{ display: 'none' }}
                  />
                  {opt.replace(/_/g, ' ')}
                </label>
              ))}
            </div>

            {resolution === 'use_merged' && (
              <>
                <label style={labelStyle}>Merged Data (JSON)</label>
                <textarea
                  value={mergedJson}
                  onChange={e => { setMergedJson(e.target.value); setResolveError(''); }}
                  style={{
                    width: '100%', minHeight: 200, fontFamily: 'monospace', fontSize: '0.8rem',
                    padding: '0.75rem', borderRadius: 6, border: '1px solid #d1d5db', resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </>
            )}

            {resolveError && (
              <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{resolveError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={() => setResolveModal(null)} style={btnStyle}>Cancel</button>
              <button
                onClick={handleResolve}
                disabled={actionLoading === 'resolve'}
                style={{ ...btnStyle, backgroundColor: ACCENT, color: '#fff', borderColor: ACCENT }}
              >
                {actionLoading === 'resolve' ? 'Resolving...' : 'Resolve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getDifferingFields(local: Record<string, unknown>, external: Record<string, unknown>): string[] {
  const metaFields = new Set(['id', 'org_id', 'type', 'created_at', 'updated_at', 'external_id']);
  const diff: string[] = [];
  for (const key of Object.keys(external)) {
    if (metaFields.has(key)) continue;
    if (local[key] !== undefined && JSON.stringify(local[key]) !== JSON.stringify(external[key])) {
      diff.push(key);
    }
  }
  return diff;
}

const selectStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
  backgroundColor: '#fff',
};

const thStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  fontSize: '0.8rem',
};

const tdStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  color: '#111827',
};

const btnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontWeight: 500,
};

const smallBtnStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderRadius: 4,
  border: '1px solid',
  backgroundColor: 'transparent',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: 500,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  marginBottom: 4,
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
  width: 140,
};

const preStyle: React.CSSProperties = {
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '0.75rem',
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  overflow: 'auto',
  maxHeight: 300,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
